// Wave 3 billing: the credit packs, inline. `PackList` is the list with Buy buttons (the Credits page uses it too);
// `PacksSheet` puts it in a sheet with a line for why it opened; `PacksHost` (mounted once in App) shows the sheet
// whenever src/packs.ts openPacks() asks, from a tap, the Director's `open_packs`, a 402 from any paid call, or the
// guided tour; `LowBalancePill` is the "Running low: N credits left" note with Buy credits (dismissed per session).
// Buy -> POST credits/checkout -> a full redirect to Stripe -> /api/credits/confirm -> /credits?ok=1 (as before).
import { useEffect, useState, useSyncExternalStore } from "react";
import { api } from "../api";
import { isCountName } from "../credits";
import { director } from "../director";
import { CHECKOUT_NOTE } from "../legal/guard";
import { closePacks, dismissLowBalance, lowBalanceDismissed, lowBalanceText, openPacks, packsLine, packsOpens, packsRequest, subscribePacks, type PacksRequest } from "../packs";
import { navigate } from "../router";
import { toast, useStore } from "../store";
import type { CreditPack } from "../types";
import { ErrorNote, fmtCredits, fmtUsd, Icon, Sheet, Spinner, useLoad } from "./bits";

/** Opens Stripe Checkout for a pack: a full redirect (Stripe is another origin). Throws with a message to show. */
export async function startCheckout(packId: string): Promise<void> {
  const { url } = await api.checkout(packId);
  if (!url) throw new Error("Checkout didn't return a link.");
  // Stripe Checkout comes back to /api/credits/confirm, then /credits?ok=1
  if (url.startsWith("/")) navigate(url);
  else location.assign(url);
}

/**
 * The packs on sale with a Buy button each (price, credits, a one-line blurb). Loads GET credits/packs itself unless
 * `packs` is given. `onBuy` runs before the redirect (e.g. to tell the Director). Keeps the Credits page's
 * data-action ids ("credits.buy") so the help highlights and tests still find them.
 */
export function PackList({ onBuy, compact = false }: { onBuy?: (p: CreditPack) => void; compact?: boolean }) {
  const { data, error } = useLoad(() => api.packs(), []);
  const [buying, setBuying] = useState<string | null>(null);
  const packs = data?.packs;
  const canBuy = data?.enabled !== false;
  const buy = async (p: CreditPack) => {
    setBuying(p.id);
    try {
      onBuy?.(p);
      await startCheckout(p.id);
    } catch (e) {
      toast(`Couldn't open checkout. ${(e as Error).message}`, "bad");
      setBuying(null);
    }
  };
  return (
    <>
      {error && <ErrorNote text={`Couldn't load the packs. ${error}`} />}
      {!packs && !error && <Spinner label="Loading packs" />}
      <ul className={`pack-list${compact ? " pack-list-compact" : ""}`}>
        {packs?.map((p) => (
          <li key={p.id} className="pack">
            <div className="pack-info">
              <p className="pack-credits">
                {fmtCredits(p.credits)} <span>credits</span>
              </p>
              <p className="pack-name">
                {/* a top-up's name is just its credit count, already said in big type above */}
                {!isCountName(p.name) && p.name}
                {p.note && <span>{p.note}</span>}
              </p>
            </div>
            <button data-action="credits.buy" type="button" className="btn btn-primary" onClick={() => void buy(p)} disabled={!!buying || !canBuy}>
              {buying === p.id ? "Opening" : `Buy for ${fmtUsd(p.usd)}`}
            </button>
          </li>
        ))}
      </ul>
      {data && (
        <p className="test-note">
          {!canBuy ? "Buying credits is switched off on this server (no Stripe key yet)." : data.testMode !== false ? "Payments run in Stripe test mode. Use card 4242 4242 4242 4242, any future date and any CVC." : "Payment is by card through Stripe. Credits land as soon as it's done."}
        </p>
      )}
      <p className="muted pack-terms">
        {CHECKOUT_NOTE.before} <a href={CHECKOUT_NOTE.link.path}>{CHECKOUT_NOTE.link.text}</a> {CHECKOUT_NOTE.after}
      </p>
    </>
  );
}

/** The packs in a sheet, over whatever is on screen. `request` says why it opened (src/packs.ts PacksRequest). */
export function PacksSheet({ request, onClose }: { request: PacksRequest; onClose: () => void }) {
  const balance = useStore((s) => s.balance);
  const line = packsLine({ ...request, balance: request.balance ?? balance ?? undefined }, fmtCredits);
  return (
    <Sheet title="Buy credits" onClose={onClose} className="packs-sheet">
      <div className="sheet-body">
        <p className="packs-sheet-bal">
          <Icon name="credits" size={18} /> Balance <strong>{fmtCredits(balance)}</strong>
        </p>
        {line && (
          <p className={`packs-sheet-line${request.reason === "out" ? " is-out" : ""}`} role="status">
            {line}
          </p>
        )}
        <PackList compact onBuy={(p) => director.context(`The user tapped Buy on the ${p.name} pack (${fmtUsd(p.usd)}); Stripe Checkout is opening. When they come back the credits are added.`)} />
      </div>
    </Sheet>
  );
}

/** Mounted once (App): shows the sheet whenever openPacks() is called; tells the Director when it opens or closes. */
export function PacksHost() {
  const req = useSyncExternalStore(subscribePacks, packsRequest, () => null);
  const opens = useSyncExternalStore(subscribePacks, packsOpens, () => 0);
  useEffect(() => {
    if (!req || req.source === "director") return; // the Director opened it: it knows
    director.context(req.reason === "out" ? "The credit packs opened on screen because a paid step needs more credits than they have." : "The user opened the credit packs on screen.");
  }, [opens]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!req) return null;
  return <PacksSheet key={opens} request={req} onClose={closePacks} />;
}

/** "Running low: N credits left" with Buy credits; dismissed for the rest of the browser session. Hidden at 200+. */
export function LowBalancePill() {
  const balance = useStore((s) => s.balance);
  const dismissed = useSyncExternalStore(subscribePacks, lowBalanceDismissed, () => false);
  const text = lowBalanceText(balance, dismissed, fmtCredits);
  if (!text) return null;
  return (
    <div className="lowbal" role="status" data-help="credits.low">
      <Icon name="credits" size={16} />
      <span className="lowbal-text">{text}</span>
      <button type="button" className="btn btn-primary btn-small" data-action="credits.low-buy" onClick={() => openPacks({ reason: "low", balance: balance ?? undefined, source: "tap" })}>
        Buy credits
      </button>
      <button type="button" className="icon-btn lowbal-x" aria-label="Dismiss for now" onClick={dismissLowBalance}>
        <Icon name="close" size={16} />
      </button>
    </div>
  );
}
